import { useGlobalStore } from '@/frontend/store/GlobalStore';
import { I18n } from '@/i18n/I18n';
import { FileTypes } from '@/types/fileTypes';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Check, X } from 'lucide-react';
import { EvidenceSubmission, ReviewerOutcome } from '@/backend/models/evidenceSubmission';
import { RequirementParams } from '@/backend/access/requirement/types';
import { ReviewerOutcomeModal } from '../requirements/ReviewerOutcomeModal';
import { SubmissionInformation } from '../requirements/SubmissionInformation';
import { EvidenceItem } from '@/backend/models/evidenceItem';
import { RequirementCategoryType } from '@/backend/access/requirementEvidenceCategory/types';
import { FilePreviewContent } from './FilePreviewContent';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { VerificationCriteriaCard } from './VerificationCriteriaCard';

interface FilePreviewModalProps {
  evidenceItem: EvidenceItem;
  onHide: () => void;
  requirement: RequirementParams;
  submission: EvidenceSubmission;
}

export function EvidenceItemPreviewModal({ evidenceItem, onHide, requirement, submission }: FilePreviewModalProps) {
  const userClass = useGlobalStore((s) => s.userClass);
  const canReview = requirement.propelOrgId === userClass?.getOrgId() && !submission.reviewerOutcome && evidenceItem.type === RequirementCategoryType.EVIDENCE;
  const [reviewModal, setReviewModal] = useState<{ outcome: ReviewerOutcome; label: string } | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const acceptDisabled = evidenceItem.file.type === FileTypes.PDF && !hasScrolledToBottom;
  const verificationCriteria = useRequirementsStore((s) => s.verificationCriteria);
  const fetchAndSortVerificationCriteria = useRequirementsStore((s) => s.fetchAndSortVerificationCriteria);

  useEffect(() => {
    void fetchAndSortVerificationCriteria(requirement.id);
  }, [requirement.id]);

  return (
    <Modal show onHide={onHide} dialogClassName="file-preview-dialog" contentClassName="file-preview-modal">
      <div className="file-preview-with-sidebar">
        <div className="file-preview-sidebar">
          <div style={{ padding: '19px' }}>
            <button className="fusa-btn-medium-icon file-preview-close-btn" onClick={onHide} aria-label={I18n.get('close')}>
                <X size={20} />
            </button>
            <div className="mt-4">
              <h3 className="file-preview-sidebar-title">{submission.title}</h3>
              <p className="file-preview-sidebar-filename pb-1">{evidenceItem.file.fileName}</p>
              {evidenceItem.type === RequirementCategoryType.EVIDENCE && <SubmissionInformation submission={submission} />}
            </div>
          </div>
          {verificationCriteria.length > 0 && (
            <div className="vc-sidebar-section">
              {verificationCriteria.map((vc) => (
                <VerificationCriteriaCard key={vc.id} criteria={vc} />
              ))}
            </div>
          )}
          {canReview && (
            <div className="file-preview-sidebar-actions">
              <button
                className="fusa-btn-medium-with-icon fusa-btn-white"
                disabled={acceptDisabled}
                title={acceptDisabled ? I18n.get('scrollToReviewPdf') : undefined}
                onClick={() => setReviewModal({ outcome: ReviewerOutcome.APPROVED, label: I18n.get('acceptEvidence') })}
              >
                <Check size={20} />
                {I18n.get('acceptEvidence')}
              </button>
              <button
                className="fusa-btn-medium-with-icon fusa-btn-danger"
                onClick={() => setReviewModal({ outcome: ReviewerOutcome.REJECTED, label: I18n.get('rejectEvidence') })}
              >
                <X size={20} />
                {I18n.get('rejectEvidence')}
              </button>
            </div>
          )}

          {reviewModal && (
            <ReviewerOutcomeModal
              outcomeLabel={reviewModal.label}
              submissionId={submission.id}
              outcome={reviewModal.outcome}
              onHide={() => setReviewModal(null)}
            />
          )}
        </div>
        <FilePreviewContent file={evidenceItem.file} onScrolledToBottom={() => setHasScrolledToBottom(true)} />
      </div>
    </Modal>
  );
}
